<template>
  <div
    class="mt-4 md:mt-6 bg-white rounded-lg border hover:bg-gray-brand-100 justify-center overflow-hidden"
    :class="cardShadowClasses"
  >
    <div class="mx-auto">
      <div class="mx-auto space-y-8">
        <div class="bg-white">
          <div class="flex items-center border-b border-gray-200 p-4">
            <dl class="grid flex-1 gap-x-6 text-sm grid-cols-2 col-span-2">
              <div>
                <dt class="font-semibold text-gray-brand-900 tracking-tight uppercase text-xs">ORDER</dt>
                <dd class="mt-1 text-gray-500">{{ cart.display_id || `#${cart.bottle_id}` }}</dd>
              </div>
              <div>
                <dt class="font-semibold text-gray-brand-900 tracking-tight uppercase text-xs">SUBTOTAL</dt>
                <dd class="mt-1 font-medium text-gray-900">{{ cart.total_formatted?.format }}</dd>
              </div>
            </dl>
          </div>

          <h4 class="sr-only">Items</h4>
          <div :class="hasGroups ? 'flex flex-col gap-4 my-4' : ''">
            <div
              v-for="(items, group) in groupedCartItems"
              :key="group"
              :class="hasGroups ? 'border rounded-lg mx-2' : ''"
            >
              <div v-if="hasGroups" class="text-sm px-2 py-1 rounded-t-lg border-b bg-gray-50 mb-1">
                Items for
                <span class="font-bold">{{ formatGroupDate(group) }}</span>
              </div>
              <ul role="list" class="divide-y divide-gray-200">
                <li v-for="item in items" :key="item.category_product_store_pair_id" class="px-6 py-4">
                  <div class="min-w-0 flex-1 flex items-start">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start">
                        <span
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-500 text-brand-100 mr-2"
                        >
                          {{ item.quantity }}
                        </span>
                        <p class="text-sm font-medium text-brand-gray-800 max-w-sm text-left">
                          <span class="font-medium">
                            {{ item.product_name }}
                          </span>
                          <template v-if="itemVariantOptions(item).length > 0">
                            <br />
                            <template v-for="(option, index) in itemVariantOptions(item)" :key="index">
                              <span class="font-light text-xs">
                                {{ option.name }}{{ option.description ? ` (${option.description})` : '' }}
                              </span>
                              <span v-if="(option.quantity || 0) > 1" class="font-light text-xs">
                                x {{ option.quantity }}
                              </span>
                              <br />
                            </template>
                          </template>
                        </p>
                      </div>
                    </div>
                    <div class="ml-4 self-start">
                      <div class="text-xs text-right">
                        <span class="font-medium">{{ formatPrice(item.total_price) }}</span>
                        <br />
                        <span class="font-light">
                          <span v-if="shouldStrike(item)">
                            <s>{{ strikethroughUnitPrice(item) }}</s>
                          </span>
                          {{ activeUnitPrice(item) }}
                          ({{ item.quantity }})
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div class="p-4">
            <PrimaryButton
              @click="$emit('proceed', cart.bottle_id)"
              class="w-full"
              :size="'xl'"
              :streaking="true"
              :justify="'justify-center'"
            >
              Proceed with Order &rarr;
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import { cardShadowClasses } from '@/config'
import { serverDayToDateObject } from '@/helpers/dates'
import { formatPrice } from '@/composables/useCartMutation'

export default {
  components: { PrimaryButton },

  props: {
    cart: {
      type: Object,
      required: true
    }
  },

  emits: ['proceed'],

  data: () => ({
    cardShadowClasses
  }),

  computed: {
    groupedCartItems() {
      const items = this.cart?.cart_items || []
      const sorted = _.sortBy(items, (i) => i.fulfillment_group_date || '')
      return _.groupBy(sorted, (i) => i.fulfillment_group_date || 'default')
    },
    hasGroups() {
      const keys = Object.keys(this.groupedCartItems).filter((k) => k !== 'default')
      return keys.length > 1
    }
  },

  methods: {
    formatPrice,
    formatGroupDate(group) {
      if (!group || group === 'default') return ''
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      }).format(serverDayToDateObject(group))
    },
    itemVariantOptions(item) {
      const options = []
      for (const variant of item.variants || []) {
        for (const vo of variant.variant_options || []) {
          if (vo.name) {
            options.push({ name: vo.name, description: vo.description, quantity: vo.quantity })
          }
        }
      }
      return options
    },
    // Mirror V2CartItems.vue's strikethrough/unit-price block: read
    // `display_price` (set by CartPricingService) and only strike when the
    // pricing-tier-adjusted price differs from the default.
    shouldStrike(item) {
      const dp = item.display_price
      if (!dp || dp.price == null || dp.strike_through_price == null) return false
      return dp.price !== dp.strike_through_price
    },
    strikethroughUnitPrice(item) {
      const dp = item.display_price
      return dp?.strike_through_price_formatted?.format || formatPrice(dp?.strike_through_price)
    },
    activeUnitPrice(item) {
      const dp = item.display_price
      if (dp) {
        return dp.price_formatted?.format || formatPrice(dp.price)
      }
      return formatPrice(Math.round(item.total_price / item.quantity))
    }
  }
}
</script>
